import { NodeType } from '@/enums/node-type.enum';
import {
  isFileDefault,
  isPromptDefault,
  isTextDefault,
  type ToolConfig,
  type ToolConfigInputDefault,
} from '@/types/tool-config.types';
import { assetUrl } from '@/utils/assetUrls';
import type { ResolvedAssets } from '@/types/api/assets';
import type { DesignAppInput } from '@/types/design-app.types';
import type { NodeId } from 'web';

type PreviewFile = {
  url?: string;
  blobstore?: { id?: string };
};

function previewFile(value: DesignAppInput['value'] | undefined): PreviewFile | null | undefined {
  if (!value || typeof value !== 'object' || !('file' in value)) {
    return undefined;
  }
  return (value as { file?: PreviewFile | null }).file;
}

/** A cleared uploader stores `{ url: '' }`. That is not a last-fill, so the stored default can show. */
export function hasFilledImportFile(value: DesignAppInput['value'] | undefined): boolean {
  const file = previewFile(value);
  if (!file || typeof file !== 'object') {
    return false;
  }
  return Boolean(file.url || file.blobstore?.id);
}

/** A blobstore-only default has no delivery URL. Run still needs the signed URL the preview already resolved. */
export function withResolvedFileUrl(value: DesignAppInput['value'], resolved: ResolvedAssets): DesignAppInput['value'] {
  const file = previewFile(value);
  if (!file || typeof file !== 'object' || file.url) {
    return value;
  }
  const url = assetUrl(file, resolved);
  if (!url) {
    return value;
  }
  return {
    ...(value as object),
    file: { ...file, url },
  } as DesignAppInput['value'];
}

/**
 * The value the tool preview should show for a stored default.
 * Absent means inherit the node. A prompt payload applies only to prompt nodes, a text payload only to
 * text nodes, and a file payload only to import nodes, matching inspect.
 */
export function toolPreviewValue(
  nodeType: string,
  stored: ToolConfigInputDefault | undefined,
): DesignAppInput['value'] | undefined {
  if (stored === undefined) {
    return undefined;
  }
  if (isPromptDefault(stored) && (nodeType === NodeType.Prompt || nodeType === NodeType.PromptV3)) {
    return { prompt: stored.prompt };
  }
  if (isTextDefault(stored) && nodeType === NodeType.String) {
    return { string: stored.string };
  }
  if (isFileDefault(stored) && nodeType === NodeType.Import) {
    if (stored.file === null) {
      return { file: null } as unknown as DesignAppInput['value'];
    }
    return {
      file: {
        ...stored.file,
        ...(stored.type ? { type: stored.type } : {}),
      },
    } as DesignAppInput['value'];
  }
  return undefined;
}

/**
 * Preview display overrides from versioned `toolConfig.inputs`.
 * Order is array position. A string `description` (including `""`) overrides the node; absence keeps
 * the node fallback. `exposed: false` hides the input from Preview entirely (no parameters drawer).
 * Missing config entries leave every field undefined so callers keep the metadata/node fallback.
 */
export function toolPreviewInputDisplay(
  nodeId: NodeId,
  toolConfig: ToolConfig | null | undefined,
): { description?: string; order?: number; exposed?: boolean; required?: boolean } {
  const inputs = toolConfig?.inputs;
  if (!inputs) {
    return {};
  }
  const index = inputs.findIndex((entry) => entry.nodeId === nodeId);
  if (index < 0) {
    return {};
  }
  const entry = inputs[index];
  return {
    order: index,
    ...(typeof entry.exposed === 'boolean' ? { exposed: entry.exposed } : {}),
    ...(typeof entry.description === 'string' ? { description: entry.description } : {}),
    ...(entry.required === true ? { required: true } : {}),
  };
}
